import RenderManager from "../rendering/render-manager";
import Utils from "../utils";
import SlideManager from "../utils/slide-manager";
import getVWKey from "../helpers/getVWKey";
import MaterialBuilder from "../rendering/material-builder";
import applyDefaultOrder from "../layers/DefaultOrder";
import { fire } from "../utils/events";
import calcScreenspaceBoundingbox from "../utils/calc-screenspace-bbox";
import calcInnerCircle from "../utils/calc-inner-circle";
import LayersManager from "../layers/LayersManager";
import QuizManager from "../quizes/quiz-manager";

let material, materialWireframe;

const composite = new THREE.Matrix4(), inverse = new THREE.Matrix4();
const tmpInvere = new THREE.Matrix4();
const isDebugVisible = Utils.queryParams.has("debug_plop");
const RADIUS_MULTIPLIER = 0.75;
const stateHolder = new WeakMap();
const tmpNullVec = new THREE.Vector3();
const tmpNullQuat = new THREE.Quaternion();
const tmpPlane = new THREE.Plane();
const tmpVec1 = new THREE.Vector3(), tmpVec2 = new THREE.Vector3();
const tmpDiff = new THREE.Vector3();

const DISPLAY_OUTLINE = true;

class AssessmentMeshPlop extends THREE.EventDispatcher {

    getCurrentInitialMatrix() { return getVWState(this._initialMatrices).matrix; }

    get radiusMultiplier() { return RADIUS_MULTIPLIER; }
    get originalState() { return this._originalState; }

    constructor({
        object,
        matrix = object.matrix,
        sizeSm = { matrix },
        isWidget3d = false,
        onPlop, onPickup,
        originalState = null }) {
        super();

        material = material || new THREE.MeshBasicMaterial({ color: 0xcccccc, transparent: true, opacity: 0.5 });
        materialWireframe = materialWireframe || MaterialBuilder.build({ color: 0x0c0c0c, transparent: true, opacity: 0.5 });

        this.isPlopAssessment = true;
        this.disableInteraction = false;

        this._onPlop = onPlop;
        this._onPickup = onPickup;

        /**
         * @type {THREE.Mesh | THREE.Group}
         */
        this._object = object;
        this._isWidget3d = isWidget3d;

        this._initialMatrices = Object.freeze({
            matrix: new THREE.Matrix4().copy(matrix),
            sizeSm: { matrix: new THREE.Matrix4().copy(sizeSm.matrix) }
        });

        this._originalState = originalState
            ? originalState
            : Object.assign(
                matrixToState(this._initialMatrices.matrix), {
                sizeSm: matrixToState(this._initialMatrices.sizeSm.matrix)
            });

        function createObject(object) {
            if (object.isMesh) {
                const visual = new THREE.Mesh(object.geometry, material);
                const wireframe = new THREE.Mesh(object.geometry, materialWireframe);

                visual.add(wireframe);
                object.matrix.decompose(visual.position, visual.quaternion, visual.scale);

                object.geometry.computeBoundingSphere();
                object.geometry.computeBoundingBox();

                wireframe.isTemporary = visual.isTemporary = true;
                return visual;
            } else {
                const group = new THREE.Group();
                group.isTemporary = true;

                object.matrix.decompose(group.position, group.quaternion, group.scale);

                return group;
            }
        }

        this._plop = createObject(object);
        this._plop.name = `__${object.name}_plop__`;

        function constructGroup(object, parent) {
            object.children.forEach(object => {
                const _parent = createObject(object);
                parent.add(_parent);

                constructGroup(object, _parent);
            });
        }

        constructGroup(object, this._plop);

        this._plop.dontShowInHierarchyPanel = true;

        this.getParentChain(object)[0].add(this._plop);

        if (!(Q3.editMode || isDebugVisible)) this._plop.visible = false;

        const vpKey = getVWKey();
        const currentMatrix = vpKey === "sizeSm" ? sizeSm.matrix : matrix;

        currentMatrix.decompose(this._plop.position, this._plop.quaternion, this._plop.scale);

        this._plop.updateMatrixWorld();

        composite.multiplyMatrices(object.parent.matrixWorld, currentMatrix);
        inverse.getInverse(composite);

        this._bbox = new THREE.Box3();

        const tmpVec = new THREE.Vector3();

        this._plop.traverse(obj => {
            if (!obj.isMesh) return;

            const attrPosition = obj.geometry.getAttribute("position");

            for (let i = 0, len = attrPosition.count; i < len; i++) {
                tmpVec
                    .fromBufferAttribute(attrPosition, i)
                    .applyMatrix4(obj.matrixWorld)
                    .applyMatrix4(inverse);
                this._bbox.expandByPoint(tmpVec);
            }
        });

        if (isDebugVisible) this._object.add(new THREE.Box3Helper(this._bbox));

        this._size = this._bbox.getBoundingSphere(new THREE.Sphere()).radius;
        this._plopped = false;
        this._plopAssessment = null;
        this._plopAttachment = null;
        this._layer = createAssessmentLayer(this);
    }

    get matrix() { return this.getCurrentInitialMatrix().toArray(); }

    getParentChain(object) {
        let parent = object.parent, rootChanged;

        const parents = [object, parent];

        do {
            rootChanged = false;

            if (parent.isMovableInverter || parent.isMovableParent) {
                rootChanged = true;
                parent = parent.parent;
                parents.push(parent);
            }
        } while (rootChanged);

        return parents.reverse();
    }

    get uuid() { return this._object.uuid; }

    toJSON() {
        const objectData = SlideManager.currentSlide.animation[this._object.uuid];
        const serialized = {
            uuid: this._object.uuid,
            matrix: this._initialMatrices.matrix.toArray(),
            originalState: this._originalState
        };

        if ("sizeSm" in objectData) serialized.sizeSm = this._initialMatrices.sizeSm.matrix.toArray();

        return serialized;
    }

    updateGeometry(geometry) {
        geometry.computeBoundingSphere();
        geometry.computeBoundingBox();

        this._plop.geometry = geometry;
        this._bbox.copy(geometry.boundingBox);
        this._size = geometry.boundingSphere.radius;
    }

    get isWidget3d() { return this._isWidget3d; }
    get object() { return this._object; }
    get plopObject() { return this._plop; }
    get boundingBox() { return this._bbox; }
    get isPlopped() { return this._plopped; }
    get isCorrectlyPlopped() {
        return this._plopped &&
            (this.uuid === this._plopAssessment?.uuid || (QuizManager.isCommutable && this._object.name === this._plopAssessment._object.name));
    }

    update() {
        const layer = this._layer.objects[0];
        if (this.isPlopped) {
            if (layer.isDropzoneVisible) layer.setDropzoneVisible(false);
        } else if (layer.isDropzoneVisi1ble !== QuizManager.displayDropzone)
            layer.setDropzoneVisible(Q3.editMode || QuizManager.displayDropzone);

        if (this._plopAttachment && layer.isControlsVisible) layer.setControlsVisible(false);
        else if (!this._plopAttachment && !layer.isControlsVisible) layer.setControlsVisible(true);

        this._layer.forceUpdate();
    }

    resetRenderState() {
        const object = this._object;
        const oldState = stateHolder.get(object);

        if (!oldState) return;

        object.renderOrder = oldState.order;
        object.onBeforeRender = oldState.oldCallback;

        stateHolder.delete(object);
    }

    saveRenderState() {
        const object = this._object;
        const oldState = { order: object.renderOrder, oldCallback: object.onBeforeRender };

        stateHolder.set(object, oldState);
        object.renderOrder = 1e10;

        object.onBeforeRender = function (renderer) {
            renderer.clearDepth();

            if (oldState.oldCallback)
                oldState.oldCallback(...arguments);
        }
    }

    /**
     * 
     * @param {AssessmentMeshPlop} assessment 
     */
    plop(assessment) {
        this._plopped = true;
        this._plopAssessment = assessment;
        assessment._plopAttachment = this;

        this.resetRenderState();

        if (QuizManager.useSnapping) {
            updatePlane(this._plop.matrixWorld);

            inverse.getInverse(assessment._object.parent.matrixWorld);

            composite
                .multiplyMatrices(inverse, this._plop.matrixWorld)
                .decompose(assessment._object.position, tmpNullQuat, tmpNullVec);

            if (this !== assessment) {
                assessment._object.updateMatrixWorld();

                tmpInvere.getInverse(assessment._object.parent.matrixWorld);

                const { center: centerPlop } = getScreenspaceCenter(this._plop, this._bbox);
                const ssCenterPlop = RenderManager.toScreenSpaceCoords(centerPlop);
                const itsctPlop = intersectPlane(ssCenterPlop, tmpVec1).applyMatrix4(tmpInvere);

                const { center: centerObject } = getScreenspaceCenter(assessment._object, assessment._bbox);
                const ssCenterObject = RenderManager.toScreenSpaceCoords(centerObject);
                const itsctObject = intersectPlane(ssCenterObject, tmpVec2).applyMatrix4(tmpInvere);

                tmpDiff.subVectors(itsctPlop, itsctObject)

                assessment._object.position.add(tmpDiff);
            }
        }

        if (this._onPlop) this._onPlop();
    }

    pickup(object) {
        if (this._plopAttachment) {
            this._plopAttachment._plopped = false;
            this._plopAttachment._plopAssessment = null;
            this._plopAttachment = null;
        }

        this._layer.objects[0].disableControls();
        if (this._onPickup) this._onPickup();

        this.saveRenderState();
    }

    release(object = this._object) {
        this.resetRenderState();

        if (this._plopAttachment) {
            this._plopAttachment._plopped = false;
            this._plopAttachment._plopAssessment = null;
            this._plopAttachment = null;
        }

        this._layer.objects[0].enableControls();

        if (QuizManager.useSnapping) {
            const source = getSourcePosition(object);

            object.position.copy(source.position);
        }
    }

    check(mouse, object) {
        const intersections = SlideManager
            .currentSlide.slideQuiz.assessments
            .filter(({ isPlopped }) => !isPlopped)
            .map(ass => {
                const c1 = getScreenspaceCenter(this._object, this._bbox);
                const c2 = getScreenspaceCenter(ass._plop, ass._bbox);
                const r1 = c1.radius * RADIUS_MULTIPLIER, r2 = c2.radius * RADIUS_MULTIPLIER;
                const distSq = (c1.center.x - c2.center.x) * (c1.center.x - c2.center.x) +
                    (c1.center.y - c2.center.y) * (c1.center.y - c2.center.y);
                const radSumSq = (r1 + r2) * (r1 + r2);

                return { distSq, radSumSq, assessment: ass, isCorrect: distSq <= radSumSq };
            })
            .filter(({ isCorrect }) => isCorrect)
            .sort(({ distSq: a }, { distSq: b }) => a - b);

        if (intersections.length > 0) {
            const { assessment, isCorrect } = intersections[0];

            if (isCorrect) assessment.plop(this);
        }
    }

    _changePlaces(assessment) {
        const source = getSourcePosition(this._object);

        this._object.position.copy(source.position);
        this._object.matrix.compose(this._object.position, tmpNullQuat, tmpNullVec);
        this._object.matrixWorld.multiplyMatrices(this._object.parent.matrixWorld, this._object.matrix);

        [
            [this._initialMatrices.matrix, assessment._initialMatrices.matrix],
            [this._initialMatrices.sizeSm.matrix, assessment._initialMatrices.sizeSm.matrix]
        ].forEach(([mat1, mat2]) => {
            const tmpMatrix = composite.copy(mat1);
            mat1.copy(mat2);
            mat2.copy(tmpMatrix);
        });

        flipObjects(this._plop, assessment._plop);
    }

    restoreToOriginal() {
        const object = this._object;
        const animData = SlideManager.currentSlide.animation[object.uuid];
        const state = getVWState(this._originalState);

        object.position.set(
            state.position.x,
            state.position.y,
            state.position.z
        );

        Object.assign(animData.position, {
            x: object.position.x,
            y: object.position.y,
            z: object.position.z
        });

        object.updateMatrixWorld();
    }

    setSolved(isCorrect) {
        this.disableInteraction = true;
        this._layer.objects.forEach(x => x.setSolved(isCorrect));

        if (DISPLAY_OUTLINE) {
            this._setObjectOutline(isCorrect ? 'correct' : 'wrong');
        }
    }

    hideSolved() {
        this.disableInteraction = true;
        this._layer.objects.forEach(x => x.hideSolved());

        if (DISPLAY_OUTLINE) {
            this._setObjectOutline(null);
        }
    }

    _setObjectOutline(highlight) {
        this._object.traverseVisible(obj => {
            if (obj.isMesh) {
                RenderManager.setQuizHighlightObject(obj, highlight);
            }
        });
    }

    destroy() {
        if (this._plop.parent) this._plop.parent.remove(this._plop);

        if (DISPLAY_OUTLINE) {
            this._setObjectOutline(null);
        }

        this.restoreToOriginal();

        this._layer.delete();
        LayersManager.update();
    }
}

export default AssessmentMeshPlop;
export { AssessmentMeshPlop };

/**
 * 
 * @returns {import("../layers/Layer").Layer}
 */
function createAssessmentLayer(ass) {
    const subtype = "assessment-plops";
    const uuid = THREE.Math.generateUUID();
    const index = Q3.layers.graph.length;
    const layer = new Q3.Layer({
        type: "2D",
        isText: false,
        subtype,
        temporary: true,
        index,
        reordered: false,
        name: ass._object.name
    }, true);

    Q3.layers.list[layer.uuid] = layer;
    Q3.layers.update();

    const assessment = new Q3.PlopAssessment({
        subtype: "assessment-plop",
        assessment: ass,
        visibleDropzone: Q3.editMode || QuizManager.displayDropzone,
        visibleControls: true
    }, layer, uuid);

    layer.objects[0] = assessment;
    layer.level = 1;

    applyDefaultOrder(Q3.layers.graph, layer.uuid);
    fire("create_finish", { type: "2d", uuid: layer.uuid });

    return layer;
}

function getSourcePosition(object) {
    const vpKey = getVWKey();
    const slideData = SlideManager.currentSlide.animation[object.uuid];
    const source = vpKey === "sizeSm" && slideData[vpKey] ? slideData[vpKey] : slideData;

    return source;
}

function flipObjects(a, b) {
    const tmpMatrix = composite.copy(a.matrixWorld);

    a.matrix
        .getInverse(a.parent.matrixWorld)
        .multiply(b.matrixWorld)
        .decompose(
            a.position,
            tmpNullQuat,
            tmpNullVec
        );

    b.matrix
        .getInverse(b.parent.matrixWorld)
        .multiply(tmpMatrix)
        .decompose(
            b.position,
            tmpNullQuat,
            tmpNullVec
        );

    a.matrixWorld.multiplyMatrices(a.parent.matrixWorld, a.matrix);
    b.matrixWorld.multiplyMatrices(b.parent.matrixWorld, b.matrix);
}

const matrixToState = (function () {
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const rotation = new THREE.Euler()

    return function matrixToState(matrix) {
        matrix.decompose(position, quaternion, scale);
        rotation.setFromQuaternion(quaternion);

        return { position: { x: position.x, y: position.y, z: position.z } };
    };
})();

function getVWState(state) {
    const vpKey = getVWKey();
    const object = vpKey === "sizeSm" ? state[vpKey] : state;

    return object;
}
const updatePlane = (function () {
    const tmpPosition = new THREE.Vector3();

    return function updatePlane(matrixWorld) {
        RenderManager.activeCamera.getWorldDirection(tmpPlane.normal).negate();
        tmpPosition.setFromMatrixPosition(matrixWorld);
        tmpPlane.constant = -tmpPosition.dot(tmpPlane.normal);
    };
})();

const getScreenspaceCenter = (function () {
    const tmpBoxv2 = new THREE.Box2();
    const tmpBoxv3 = new THREE.Box3();

    return function getScreenspaceCenter(object, bbox) {
        tmpBoxv3.copy(bbox).applyMatrix4(object.matrix).applyMatrix4(object.parent.matrixWorld);

        calcScreenspaceBoundingbox(tmpBoxv3, RenderManager.activeCamera, tmpBoxv2);

        return calcInnerCircle(tmpBoxv2);
    };
})();

const intersectPlane = (function () {
    const tmpLine = new THREE.Line3();
    const tmpRaycaster = new THREE.Raycaster();

    return function intersectPlane(position, out) {
        const camera = RenderManager.activeCamera;

        tmpRaycaster.setFromCamera(position, camera);

        tmpLine.start.copy(tmpRaycaster.ray.origin);
        tmpLine.end.copy(tmpRaycaster.ray.direction)
            .multiplyScalar(camera.far)
            .add(tmpRaycaster.ray.origin);

        const itsct = tmpPlane.intersectLine(tmpLine, out);

        return itsct;
    };
})();